// API client extension for LOS endpoints
import { api } from './api';

export const losApi = {
  // Loan files
  listLoans: (params = {}) => api.get('/los/loans', { params }).then(r => r.data),
  // Loans page (ticket 192): filtered, sorted and paged on the server; slim rows + status_counts.
  listLoanPage: (params = {}) => api.get('/los/loans/list', { params }).then(r => r.data),
  // Post-closing checklist + archive (ticket 192, D29): tick an item, owner/admin reopen with a reason.
  postClosing: (id) => api.get(`/los/loans/${id}/post-closing`).then(r => r.data),
  setPostClosingItem: (id, item, done) => api.put(`/los/loans/${id}/post-closing/${item}`, { done }).then(r => r.data),
  reopenLoan: (id, reason) => api.post(`/los/loans/${id}/reopen`, { reason }).then(r => r.data),
  getLoan: (id) => api.get(`/los/loans/${id}`).then(r => r.data),
  createLoan: (data) => api.post('/los/loans', data).then(r => r.data),
  updateLoan: (id, patch) => api.patch(`/los/loans/${id}`, patch).then(r => r.data),
  deleteLoan: (id) => api.delete(`/los/loans/${id}`).then(r => r.data),

  // URLA / signatures
  updateUrla: (id, urla) => api.patch(`/los/loans/${id}/urla`, urla).then(r => r.data),
  // Signing (ticket 192, D20): snapshots + "changed since signing"; the paper-signed upload (owner/admin).
  urlaSigning: (id) => api.get(`/los/loans/${id}/urla/signing`).then(r => r.data),
  urlaOriginatorDefaults: (id) => api.get(`/los/loans/${id}/urla/originator-defaults`).then(r => r.data),
  recordWetSignature: (id, { file, signers, signedOn }) => {
    const form = new FormData();
    form.append('file', file);
    form.append('signers', signers.join(','));
    form.append('signed_on', signedOn);
    return api.post(`/los/loans/${id}/urla/wet-signature`, form).then(r => r.data);
  },
  revealSsn: (id, who = 'borrower') => api.get(`/los/loans/${id}/ssn`, { params: { who } }).then(r => r.data),

  // Rate lock + fees
  updateRateLock: (id, lock) => api.patch(`/los/loans/${id}/rate-lock`, lock).then(r => r.data),
  updateLeFees: (id, fees) => api.patch(`/los/loans/${id}/le-fees`, fees).then(r => r.data),
  // Fees as LE sections + totals + cash to close (ticket 192, D8). Preview totals an unsaved
  // draft; from-tables prices a draft from the fee tables. Neither saves.
  getFees: (id) => api.get(`/los/loans/${id}/fees`).then(r => r.data),
  previewFees: (id, fees) => api.post(`/los/loans/${id}/fees/preview`, fees).then(r => r.data),
  feesFromTables: (id, body) => api.post(`/los/loans/${id}/fees/from-tables`, body).then(r => r.data),
  // Loan Estimate PDF from the saved fees (header-only: fetch through downloadWithAuth)
  lePdfPath: (id) => `/los/loans/${id}/le-pdf`,

  // Milestones
  listMilestones: (id) => api.get(`/los/loans/${id}/milestones`).then(r => r.data),
  // occurredAt: optional 'YYYY-MM-DD' (today or earlier); the server stamps now without it.
  addMilestone: (id, key, label, notes, occurredAt) =>
    api.post(`/los/loans/${id}/milestones`, null, {
      params: { key, label, notes: notes || undefined, occurred_at: occurredAt || undefined },
    }).then(r => r.data),

  // Financial Info: a finished Income Calculator run saved to the loan; income check skip flag
  saveIncomeAnalysis: (id, calcIds) => api.post(`/los/loans/${id}/income-analysis`, { calc_ids: calcIds }).then(r => r.data),
  setIncomeCheckSkip: (id, skipped) => api.put(`/los/loans/${id}/income-check-skip`, { skipped }).then(r => r.data),

  // Audit & Dates: who did what on this loan (two tiers decided by the server); `before` pages back
  loanAudit: (id, params = {}) => api.get(`/los/loans/${id}/audit`, { params }).then(r => r.data),

  // Sidebar numbers: client needs, conditions, documents
  loanCounts: (id) => api.get(`/los/loans/${id}/counts`).then(r => r.data),

  // Conditions
  listConditions: (id, status) => api.get(`/los/loans/${id}/conditions`, { params: status ? { status } : {} }).then(r => r.data),
  addCondition: (id, body) => api.post(`/los/loans/${id}/conditions`, body).then(r => r.data),
  updateCondition: (cid, body) => api.patch(`/los/conditions/${cid}`, body).then(r => r.data),
  deleteCondition: (cid) => api.delete(`/los/conditions/${cid}`).then(r => r.data),

  // Doc requests
  listDocRequests: (id) => api.get(`/los/loans/${id}/doc-requests`).then(r => r.data),
  addDocRequest: (id, body) => api.post(`/los/loans/${id}/doc-requests`, body).then(r => r.data),
  updateDocRequest: (rid, params) => api.patch(`/los/doc-requests/${rid}`, null, { params }).then(r => r.data),

  // Assignments (ticket 60 / ADR-0004)
  listAssignments: (id) => api.get(`/los/loans/${id}/assignments`).then(r => r.data),
  addAssignment: (id, body) => api.post(`/los/loans/${id}/assignments`, body).then(r => r.data),
  removeAssignment: (id, userId, slot) => api.delete(`/los/loans/${id}/assignments/${userId}`, { params: slot ? { slot } : {} }).then(r => r.data),

  // Parties + share
  listParties: (id) => api.get(`/los/loans/${id}/parties`).then(r => r.data),
  addParty: (id, body) => api.post(`/los/loans/${id}/parties`, body).then(r => r.data),
  updateParty: (pid, body) => api.patch(`/los/parties/${pid}`, body).then(r => r.data),
  sendPartyLink: (pid) => api.post(`/los/parties/${pid}/send-link`).then(r => r.data),
  deleteParty: (pid) => api.delete(`/los/parties/${pid}`).then(r => r.data),
  addPartyToThread: (pid) => api.post(`/los/parties/${pid}/add-to-thread`).then(r => r.data),
  removePartyFromThread: (pid) => api.delete(`/los/parties/${pid}/thread`).then(r => r.data),
  regenerateShareToken: (id) => api.post(`/los/loans/${id}/regenerate-share-token`).then(r => r.data),
  revokeShareToken: (id) => api.post(`/los/loans/${id}/revoke-share-token`).then(r => r.data),
  sendShareLink: (id, to) => api.post(`/los/loans/${id}/share-link/send`, { to }).then(r => r.data),

  // Borrower deal-messaging portal link
  getBorrowerLink: (dealId) => api.get(`/deals/${dealId}/borrower-link`).then(r => r.data),
  sendBorrowerLink: (dealId) => api.post(`/deals/${dealId}/borrower-link/send`).then(r => r.data),
  regenerateBorrowerLink: (dealId) => api.post(`/deals/${dealId}/borrower-link/regenerate`).then(r => r.data),

  // MISMO — API path for downloadWithAuth (ticket 185); the endpoint needs the Bearer header
  mismoPath: (id) => `/los/loans/${id}/mismo-export`,

  // AI Processor
  runAiProcessor: (id, body = {}) => api.post(`/los/ai/loans/${id}/generate-conditions`, body).then(r => r.data),
  listAiPackages: (id) => api.get(`/los/ai/loans/${id}/packages`).then(r => r.data),
  markDelivered: (pkgId, body) => api.post(`/los/ai/packages/${pkgId}/deliver`, body).then(r => r.data),
  // Suggested conditions: proposals until a person accepts one (ticket 192, D22)
  listSuggestions: (id) => api.get(`/los/ai/loans/${id}/suggestions`).then(r => r.data),
  acceptSuggestion: (sid, body = {}) => api.post(`/los/ai/suggestions/${sid}/accept`, body).then(r => r.data),
  dismissSuggestion: (sid) => api.post(`/los/ai/suggestions/${sid}/dismiss`).then(r => r.data),
  clearSuggestionCitation: (sid) => api.post(`/los/ai/suggestions/${sid}/clear-citation`).then(r => r.data),

  // Documents (uploads + generated PDFs) — Phase 3 P1
  uploadPolicy: () => api.get('/los/uploads/policy').then(r => r.data),
  listDocuments: (loanId, folder) => api.get(`/los/loans/${loanId}/documents`, { params: folder ? { folder } : {} }).then(r => r.data),
  uploadDocuments: (loanId, files, extra = {}) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append('files', f));
    if (extra.doc_request_id) form.append('doc_request_id', extra.doc_request_id);
    if (extra.folder) form.append('folder', extra.folder);
    return api.post(`/los/loans/${loanId}/documents/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  // Header-only (ticket 185): fetch through downloadWithAuth / openWithAuth / fetchBlob, never as a URL
  documentPath: (docId) => `/los/documents/${docId}/download`,
  updateDocument: (docId, body) => api.patch(`/los/documents/${docId}`, body).then(r => r.data),
  deleteDocument: (docId) => api.delete(`/los/documents/${docId}`).then(r => r.data),

  // Pre-Approval PDF generation
  preApprovalSettings: (loanId) => api.get(`/los/loans/${loanId}/pre-approval/settings`).then(r => r.data),
  savePreApprovalSettings: (loanId, settings) => api.put(`/los/loans/${loanId}/pre-approval/settings`, settings).then(r => r.data),
  generatePreApproval: (loanId, config = {}) => api.post(`/los/loans/${loanId}/pre-approval/generate`, config).then(r => r.data),
  preApprovalHistory: (loanId) => api.get(`/los/loans/${loanId}/pre-approval/history`).then(r => r.data),
  publicPdfUrl: (publicToken) => `${api.defaults.baseURL}/los/documents/public/${publicToken}`,

  // Disclosure packages + signature requests
  sendDisclosurePackage: (loanId, payload) => api.post(`/los/loans/${loanId}/disclosure-package/send`, payload).then(r => r.data),
  revokeSignLink: (loanId, sigId) => api.post(`/los/loans/${loanId}/signature-requests/${sigId}/revoke`).then(r => r.data),
  regenerateSignLink: (loanId, sigId) => api.post(`/los/loans/${loanId}/signature-requests/${sigId}/regenerate`).then(r => r.data),
  resendSignLink: (loanId, sigId) => api.post(`/los/loans/${loanId}/signature-requests/${sigId}/send`).then(r => r.data),
  listSignatureRequests: (loanId) => api.get(`/los/loans/${loanId}/signature-requests`).then(r => r.data),

  // Notifications (top-nav bell)
  listNotifications: (unreadOnly = false, limit) => api.get('/notifications', { params: { unread_only: unreadOnly, ...(limit ? { limit } : {}) } }).then(r => r.data),
  markNotificationRead: (id) => api.post(`/notifications/${id}/read`).then(r => r.data),
  markAllNotificationsRead: () => api.post('/notifications/read-all').then(r => r.data),
  dismissNotification: (id) => api.delete(`/notifications/${id}`).then(r => r.data),

  // Compliance
  loanChecks: (id) => api.get(`/compliance/loans/${id}/checks`).then(r => r.data),
  complianceDashboard: (params = {}) => api.get('/compliance/dashboard', { params }).then(r => r.data),
  // The CSV is fetched with the login token (downloadWithAuth), so this is a path, not a link.
  hotLoansCsvPath: ({ needs, q } = {}) => {
    const qs = new URLSearchParams();
    if (needs) qs.set('needs', needs);
    if (q) qs.set('q', q);
    const tail = qs.toString();
    return `/compliance/dashboard/hot-loans.csv${tail ? `?${tail}` : ''}`;
  },

  // Workspace audit log (owners and admins)
  workspaceAudit: (params = {}) => api.get('/audit/events', { params }).then(r => r.data),

  // Performance reports (ticket 193 D2): roll-ups over the pipeline, gated by reports.view.
  reportKpi: (days) => api.get('/v2/reports/kpi', { params: { days } }).then(r => r.data),
  reportFunnel: () => api.get('/v2/reports/pipeline-funnel').then(r => r.data),
  reportLeaderboard: (period) => api.get('/v2/reports/leaderboard', { params: period ? { period } : {} }).then(r => r.data),
  reportPeriods: () => api.get('/v2/reports/period-comparison').then(r => r.data),
  reportBranches: () => api.get('/v2/reports/branch-summary').then(r => r.data),

  // MCR
  listMcr: () => api.get('/compliance/mcr').then(r => r.data),
  createMcr: (body) => api.post('/compliance/mcr', body).then(r => r.data),
  getMcr: (id) => api.get(`/compliance/mcr/${id}`).then(r => r.data),
  refreshMcr: (id) => api.post(`/compliance/mcr/${id}/refresh`).then(r => r.data),
  overrideState: (id, state, overrides) => api.patch(`/compliance/mcr/${id}/state/${state}`, overrides).then(r => r.data),
  updateFc: (id, fc) => api.patch(`/compliance/mcr/${id}/financial-condition`, fc).then(r => r.data),
  finalizeMcr: (id) => api.post(`/compliance/mcr/${id}/finalize`).then(r => r.data),
  mcrCsvPath: (id) => `/compliance/mcr/${id}/export.csv`,
  mcrXmlPath: (id) => `/compliance/mcr/${id}/export.xml`,
};

// Human-readable status labels
export const LOAN_STATUS_ORDER = [
  'prospect', 'pre_qual', 'pre_approved', 'application_received',
  'processing', 'submitted_to_uw', 'conditional_approval',
  'clear_to_close', 'closing_scheduled', 'funded',
];

export const LOAN_STATUS_LABELS = {
  prospect: 'Prospect',
  pre_qual: 'Pre-Qual',
  pre_approved: 'Pre-Approved',
  application_received: 'Application Received',
  processing: 'Processing',
  submitted_to_uw: 'Submitted to UW',
  conditional_approval: 'Conditional Approval',
  clear_to_close: 'Clear to Close',
  closing_scheduled: 'Closing Scheduled',
  funded: 'Funded',
  cancelled: 'Cancelled',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
};

// Pipeline colour per loan status (matches Arive's pipeline); unknown statuses are slate.
export function statusColorHex(status) {
  const map = {
    prospect: '#94a3b8',
    pre_qual: '#3b82f6',
    pre_approved: '#6366f1',
    application_received: '#0ea5e9',
    processing: '#f59e0b',
    submitted_to_uw: '#f97316',
    conditional_approval: '#a855f7',
    clear_to_close: '#10b981',
    closing_scheduled: '#14b8a6',
    funded: '#6BA82E',
    cancelled: '#ef4444',
    denied: '#dc2626',
    withdrawn: '#ef4444',
  };
  return map[status] || '#94a3b8';
}

export { LOAN_PROGRAM_LABELS as PROGRAM_LABELS } from './shared/labels';

export const PARTY_ROLE_LABELS = {
  borrower: 'Borrower', coborrower: 'Co-Borrower',
  buyer_agent: 'Buyer Agent', listing_agent: 'Listing Agent', seller: 'Seller',
  title_company: 'Title Company', escrow: 'Escrow', insurance_agent: 'Insurance Agent',
  attorney: 'Attorney', appraiser: 'Appraiser', inspector: 'Inspector',
  builder: 'Builder', cpa: 'CPA', financial_advisor: 'Financial Advisor',
  processor: 'Processor', other: 'Other',
};
